import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Lock, Save, ShieldAlert } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const Section = ({ title, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 space-y-5" data-testid={testId}>
    <h2 className="font-display text-xl font-medium text-[#0A2540]">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

const ReadOnlyRow = ({ label, value, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
    <Label className="w-40 text-sm text-slate-600 mb-1 sm:mb-0">{label}</Label>
    <div className="flex-1 text-sm text-[#0A2540] font-medium" data-testid={testId}>{value}</div>
  </div>
);

const STATUS_LABELS = {
  active: { en: "Active", zh: "已激活" },
  pending_approval: { en: "Pending", zh: "待审核" },
  disabled: { en: "Disabled", zh: "已禁用" },
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGoogleOnly = user?.auth_provider === "google";

  // Phase 13 — forced-password-change mode (?force=1 OR user.must_change_password)
  const forceMode = Boolean(
    searchParams.get("force") === "1" || user?.must_change_password
  );

  // Phase 12 — bank tab removed; only `account` and `security` remain.
  const [tab, setTab] = useState(forceMode ? "security" : "account");

  useEffect(() => {
    if (forceMode && tab !== "security") setTab("security");
  }, [forceMode, tab]);

  // Account tab
  const [name, setName] = useState(user?.name || "");
  const [busyName, setBusyName] = useState(false);

  // Security tab
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busyPwd, setBusyPwd] = useState(false);

  const saveName = async () => {
    if (!name.trim()) {
      toast.error(t("err_generic"));
      return;
    }
    setBusyName(true);
    try {
      const r = await api.patch("/auth/me/profile", { name: name.trim() });
      setUser(r.data);
      toast.success(t("toast_saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setBusyName(false);
    }
  };

  const savePassword = async () => {
    if (newPwd.length < 8) {
      toast.error(t("password_min_8"));
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error(t("password_mismatch"));
      return;
    }
    if (!isGoogleOnly && newPwd === currentPwd) {
      toast.error(t("password_same_as_current"));
      return;
    }
    setBusyPwd(true);
    try {
      const body = isGoogleOnly
        ? { new_password: newPwd, confirm_password: confirmPwd }
        : { current_password: currentPwd, new_password: newPwd, confirm_password: confirmPwd };
      await api.post("/auth/me/password", body);
      toast.success(t("password_updated"));
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      const me = await api.get("/auth/me");
      setUser(me.data);
      // Phase 13 — once the password is set, route to the role's home
      if (forceMode) {
        const home =
          me.data.role === "admin" ? "/admin"
            : me.data.role === "reviewer" ? "/reviewer" : "/agent";
        navigate(home, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setBusyPwd(false);
    }
  };

  if (!user) return null;

  const tabs = [
    { v: "account",  label: t("account"),  icon: User },
    { v: "security", label: t("security"), icon: Lock },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 space-y-6">
        <div className="flex items-center gap-3">
          <User className="h-7 w-7 text-[#0A2540]" />
          <h1 className="font-display text-3xl lg:text-4xl font-medium text-[#0A2540]">
            {t("profile")}
          </h1>
        </div>

        {/* Phase 13 — forced password change banner */}
        {forceMode ? (
          <div
            className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4"
            data-testid="force-change-banner"
          >
            <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                {t("password_change_required")}
              </p>
              <p className="text-sm text-amber-800 mt-1">
                {t("password_change_required_body")}
              </p>
            </div>
          </div>
        ) : null}

        <div className="grid gap-6 md:grid-cols-[220px_1fr]">
          <nav className="bg-white border border-slate-200 rounded-lg p-2 h-fit" data-testid="profile-sidebar">
            {tabs.map((tt) => {
              const active = tab === tt.v;
              const locked = forceMode && tt.v !== "security";
              const Icon = tt.icon;
              return (
                <button
                  key={tt.v}
                  type="button"
                  onClick={() => !locked && setTab(tt.v)}
                  disabled={locked}
                  title={locked ? t("set_new_to_continue") : ""}
                  data-testid={`profile-tab-${tt.v}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors focus-ring ${
                    active
                      ? "bg-[#0A2540] text-white"
                      : locked
                        ? "text-slate-400 cursor-not-allowed"
                        : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-left">{tt.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="space-y-6">
            {tab === "account" ? (
              <Section title={t("account")} testId="profile-account-section">
                <ReadOnlyRow label="Email" value={user.email} testId="profile-email" />
                <ReadOnlyRow label={t("admin_col_role")} value={user.role} testId="profile-role" />
                <ReadOnlyRow
                  label={t("admin_col_status")}
                  value={(STATUS_LABELS[user.status] || {})[language] || user.status}
                  testId="profile-status"
                />
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
                  <Label className="w-40 text-sm text-slate-600 mb-1 sm:mb-0" htmlFor="profile-name">
                    {t("modal_name")}
                  </Label>
                  <div className="flex-1 flex items-center gap-3">
                    <Input
                      id="profile-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="profile-name-input"
                      maxLength={120}
                    />
                    <Button
                      onClick={saveName}
                      disabled={busyName || !name.trim() || name === user.name}
                      data-testid="profile-save-name"
                      className="bg-[#0A2540] hover:bg-[#051626] text-white"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {t("save")}
                    </Button>
                  </div>
                </div>
              </Section>
            ) : null}

            {tab === "security" ? (
              <Section
                title={isGoogleOnly ? t("set_password") : t("change_password")}
                testId="profile-security-section"
              >
                {!isGoogleOnly ? (
                  <div className="space-y-2">
                    <Label htmlFor="current-pwd">{t("current_password")}</Label>
                    <Input
                      id="current-pwd"
                      type="password"
                      value={currentPwd}
                      onChange={(e) => setCurrentPwd(e.target.value)}
                      data-testid="current-password-input"
                      autoComplete="current-password"
                    />
                  </div>
                ) : (
                  <p className="text-xs text-slate-500" data-testid="google-only-notice">
                    {t("google_only_password_notice") || "Set a password to log in with email + password too."}
                  </p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="new-pwd">{t("new_password")}</Label>
                  <Input
                    id="new-pwd"
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    data-testid="new-password-input"
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-pwd">{t("confirm_password")}</Label>
                  <Input
                    id="confirm-pwd"
                    type="password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    data-testid="confirm-password-input"
                    autoComplete="new-password"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={savePassword}
                    disabled={busyPwd || !newPwd || !confirmPwd || (!isGoogleOnly && !currentPwd)}
                    data-testid="profile-save-password"
                    className="bg-[#0A2540] hover:bg-[#051626] text-white"
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    {isGoogleOnly ? t("set_password") : t("change_password")}
                  </Button>
                </div>
              </Section>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
